import { useQueryClient } from "@tanstack/react-query";
import React from "react";
import { useNavigate, useParams } from "react-router-dom";
import {
  defaultControlledSearchQuery,
  GRID_ACTION_TYPES,
  GridActionType,
  QueryKeyTypes,
  sortBy,
} from "../../assets/types";
import { ClaimHelper } from "../../assets/utils/helpers/claimHelper";
import { Constants } from "../../assets/utils/lib/constants";
import { apiErrorMessage } from "../../assets/utils/lib/error";
import { MessageSeverity } from "../../assets/utils/lib/messageSeverity";
import { LoadingIndicator } from "../../assets/vfx/loadingIndicator";
import { useSnackbar } from "../../assets/vfx/snackbar/vfxSnackbarContext";
import { CommunityDto } from "../../services/dto/admin/communityDto";
import { IActionResponse, IActionResult } from "../../services/proxies";
import { CommunityForm } from "./communityForm";
import { CommunityPage } from "./communityPage";
import { CommunityService } from "./communityService";
import { CommunityView } from "./communityView";
import { ViewProps } from "./props";
import { useDispatch, useSelector } from "react-redux";
import { UpdateServiceRequestEnabled, UpdateSideNavbarCommunityName } from "../../stores/layoutStore";
import { SearchDto } from "../../services/dto";
import { RootState } from "../../stores/store";
import { roles } from "../../assets/configuration";

interface CommunityContainerProps { }

export const CommunityContainer: React.FC<CommunityContainerProps> = (
  props: CommunityContainerProps
) => {
  const dispatch = useDispatch();
  const { communityId } = useParams();
  const queryClient = useQueryClient();
  const { showSnackbar } = useSnackbar();
  const [data, setData] = React.useState<CommunityDto>();
  const [drawerType, setDrawerType] = React.useState<GridActionType>(
    GRID_ACTION_TYPES.HIDDEN
  );
  const communityQuery = CommunityService.getCommunityQuery(
    data?.id ?? undefined
  );
  const { isSingleCommunityUser: isSingleCommunityUserFromStore } = useSelector((state: RootState) => state.layout);

  const lookupQuery = CommunityService.getLookups();
  const { status: saveStatus, mutateAsync: saveCommunity } = CommunityService.save();
  const { status: deActivateStatus, mutate: deActivateCommunity } =
    CommunityService.deActivateCommunity();
  const navigate = useNavigate();
  const isSystemAdmin = React.useMemo(
    () => ClaimHelper.hasRole([roles.SystemAdmin]),
    []
  );

  const [searchQuery, setSearchQuery] = React.useState<SearchDto>({
    ...defaultControlledSearchQuery,
    sortBy: defaultControlledSearchQuery.sortBy ?? sortBy,
    sorts: defaultControlledSearchQuery.sorts ?? [
      defaultControlledSearchQuery.sortBy ?? sortBy,
    ],
  });

  const communityGridQuery = CommunityService.gridQuery(searchQuery, isSystemAdmin);

  function EditRecord(data: any) {
    setData(data);
    setDrawerType(GRID_ACTION_TYPES.EDIT);
  }

  function OnCustomEvent(actionType: string, dataId: string) {
    if (
      actionType === GRID_ACTION_TYPES.VIEW ||
      actionType === GRID_ACTION_TYPES.DOUBLE_CLICK
    ) {
      navigate(`/community/${dataId}`);
    }

    if (actionType === GRID_ACTION_TYPES.NEW) {
      setData(undefined);
      setDrawerType(GRID_ACTION_TYPES.NEW);
    }
  }
  function OnCloseView() {
    isSingleCommunityUserFromStore || (searchQuery?.pageNumber == 1 && communityGridQuery.data?.result.length == 1)
      ? navigate(`/adminsettings`)
      : navigate(`/community`);
  }

  const handleSubmit = async (values: CommunityDto) => {
    const isEdit = data && data.id && data.id !== Constants.emptyGuid;
    values.id = isEdit ? data.id : Constants.emptyGuid;

    const postSaveCallback = async () => {
      //dispatch(UpdateServiceRequestEnabled(values.isServiceRequestEnabled));

      if (ClaimHelper.getCommunityId()) {
        ClaimHelper.updateCommunityName(values.name);
        dispatch(UpdateSideNavbarCommunityName(values.name));
      }

      queryClient.invalidateQueries({ queryKey: ["userCommunitiesQuery"] });
    };

    return await saveCommunity(values, {
      onSuccess: (response: IActionResult<CommunityDto>) =>
        handleResponse(response, "Community Saved", postSaveCallback),
    });
  };

  const closeDrawer = () => {
    setDrawerType(GRID_ACTION_TYPES.HIDDEN);
  };

  const handleDeactivate = () =>
    deActivateCommunity(data?.id ?? Constants.emptyGuid, {
      onSuccess: (response: IActionResponse) =>
        handleResponse(response, "Community Deactivated"),
    });

  function handleResponse(
    response: IActionResponse,
    snackbarMessage: string,
    callbackfn?: Function
  ) {
    if (response) {
      if (response.isSuccess) {
        showSnackbar(snackbarMessage, MessageSeverity.Success);
        communityQuery.refetch();
        queryClient.invalidateQueries({
          queryKey: [ViewProps.CommunityQueryKeys[QueryKeyTypes.Grid]],
        });
        closeDrawer();
        callbackfn?.();
      } else {
        if (response.errorMessages) {
          const errorMsg: string | null = apiErrorMessage(
            response.errorMessages
          );
          snackbarMessage = errorMsg ?? "Error";
        }
        showSnackbar(snackbarMessage, MessageSeverity.Error);
      }
    } else {
      showSnackbar(
        "Error, unable to process your request please try again later.",
        MessageSeverity.Error
      );
    }
  }

  const renderSelectionContent = () => {
    const isDataLoaded = !lookupQuery?.isLoading;
    return isDataLoaded ? (
      <CommunityPage
        onCustomEvent={OnCustomEvent}
        lookups={lookupQuery?.data}
        query={searchQuery}
        onQueryChange={(q) => setSearchQuery(q)}
      />
    ) : (
      <LoadingIndicator isFullPage={true} />
    );
  };

  const renderViewContent = (id: string) => {
    if (data?.id !== id) {
      setData({ id: id } as CommunityDto);
    }

    const isDataLoaded = !!communityQuery?.data && !!lookupQuery?.data;
    return isDataLoaded ? (
      <CommunityView
        data={communityQuery?.data}
        show={true}
        onHide={OnCloseView}
        onDeactivate={handleDeactivate}
        onEdit={() => EditRecord(communityQuery.data)}
        lookups={lookupQuery?.data}
      />
    ) : null;
  };

  return (
    <>
      <CommunityForm
        data={data}
        show={
          drawerType === GRID_ACTION_TYPES.EDIT ||
          drawerType === GRID_ACTION_TYPES.NEW
        }
        onHide={() => setDrawerType(GRID_ACTION_TYPES.HIDDEN)}
        onSave={(data: CommunityDto) => handleSubmit(data)}
        lookups={lookupQuery?.data}
        isLoading={lookupQuery.isLoading || communityQuery.isLoading}
      />

      {communityId ? renderViewContent(communityId) : renderSelectionContent()}
    </>
  );
};
